/* eslint-disable max-len */
import { useState, type ReactNode } from 'react';
import { Check, Clock, FileText, Image as ImageIcon, PenLine } from 'lucide-react';
import { PhotoLightbox } from '@/components/PhotoLightbox';
import type { ServiceEventCompletion } from '@/types/serviceEvents';

interface WorkSummaryCardProps {
  event: ServiceEventCompletion;
}

function InfoRow({
  label,
  value,
  tabularNumeric = false,
  last = false
}: {
  label: string;
  value: ReactNode;
  tabularNumeric?: boolean;
  last?: boolean;
}) {
  return (
    <div
      className={`flex items-center justify-between py-2.5 ${last ? '' : 'border-b border-gray-100'}`}
    >
      <span className='text-sm text-gray-600'>{label}</span>
      <span
        className='text-sm font-medium text-gray-900'
        style={tabularNumeric ? { fontVariantNumeric: 'tabular-nums' } : undefined}
      >
        {value}
      </span>
    </div>
  );
}

function SignaturePill({ url, onView }: { url: string | null | undefined; onView?: () => void }) {
  if (url) {
    return (
      <button
        type='button'
        onClick={onView}
        className='inline-flex items-center gap-1 rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800 transition-colors hover:bg-green-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-green-500'
        aria-label='View captured signature'
        title='View captured signature'
      >
        <Check className='h-3 w-3' />
        Signed
      </button>
    );
  }
  return (
    <span className='inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-600'>
      Not signed
    </span>
  );
}

function SubsectionHeader({ icon, children }: { icon: ReactNode; children: ReactNode }) {
  return (
    <div
      className='mb-2 flex items-center gap-2 text-xs font-bold uppercase text-gray-500'
      style={{ letterSpacing: '0.05em' }}
    >
      {icon}
      {children}
    </div>
  );
}

// Compute service-time minutes with a defensive fallback: prefer the explicit
// hours_worked column, but if it's missing (older completions, partial data) fall
// back to (completed_at - started_at). Returns 0 when neither is usable.
function computeServiceMinutes(event: ServiceEventCompletion): number {
  if (typeof event.hours_worked === 'number' && Number.isFinite(event.hours_worked)) {
    return event.hours_worked * 60;
  }
  if (event.started_at && event.completed_at) {
    const ms = new Date(event.completed_at).getTime() - new Date(event.started_at).getTime();
    if (Number.isFinite(ms) && ms > 0) return ms / 60000;
  }
  return 0;
}

function formatServiceTime(minutes: number): string {
  if (!Number.isFinite(minutes) || minutes <= 0) return '—';
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

function HeroStat({ icon, label, value }: { icon: ReactNode; label: string; value: string }) {
  return (
    <div className='flex-1 rounded-xl border border-gray-200 bg-gray-50 px-4 py-3'>
      <div
        className='flex items-center gap-1.5 text-[10px] font-bold uppercase text-gray-500'
        style={{ letterSpacing: '0.05em' }}
      >
        {icon}
        {label}
      </div>
      <div
        className='mt-1 text-xl font-bold text-gray-900'
        style={{ fontVariantNumeric: 'tabular-nums' }}
      >
        {value}
      </div>
    </div>
  );
}

export function WorkSummaryCard({ event }: WorkSummaryCardProps) {
  // Single lightbox instance per card, fed different image sets depending on
  // which affordance was clicked (photos grid OR a signature pill). One viewer
  // means consistent UX (same zoom, swipe, keyboard) regardless of source.
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxStart, setLightboxStart] = useState(0);
  const [lightboxImages, setLightboxImages] = useState<string[]>([]);

  const photos = event.completion_photos ?? [];
  const notes = event.service_notes?.trim() ?? '';
  const hasPhotos = photos.length > 0;
  const hasNotes = notes.length > 0;

  // Captured signatures, only the ones that exist — keeps the lightbox swipe
  // from landing on an empty slide for half-signed completions.
  const signatureImages = [event.technician_signature_url, event.dealer_signature_url].filter(
    (u): u is string => !!u
  );
  const openLightbox = (images: string[], index = 0) => {
    if (images.length === 0) return;
    setLightboxImages(images);
    setLightboxStart(index);
    setLightboxOpen(true);
  };

  const mileageDisplay =
    typeof event.vehicle_mileage === 'number'
      ? `${event.vehicle_mileage.toLocaleString()} mi`
      : '—';
  const serviceMinutes = computeServiceMinutes(event);
  const serviceTimeDisplay = formatServiceTime(serviceMinutes);
  const signee = event.dealer_signee_name?.trim() || '—';

  return (
    <section className='rounded-2xl bg-white p-5 shadow-sm ring-1 ring-gray-200'>
      <h3
        className='mb-3 flex items-center gap-2 text-xs font-bold uppercase text-gray-700'
        style={{ letterSpacing: '0.05em' }}
      >
        <FileText className='h-3.5 w-3.5' />
        Work Summary
      </h3>

      {/* Hero stats — service time and mileage given equal weight as the two
          most important objective numbers about the work. */}
      <div className='mb-4 flex gap-3'>
        <HeroStat
          icon={<Clock className='h-3 w-3' />}
          label='Service Time'
          value={serviceTimeDisplay}
        />
        <HeroStat icon={<FileText className='h-3 w-3' />} label='Mileage' value={mileageDisplay} />
      </div>

      <div className='mb-5'>
        <InfoRow label='Dealer Signee' value={signee} last />
      </div>

      {/* Signatures — always rendered; "Not signed" is real information for legacy completions.
          Signed pills are clickable: launch the lightbox at the right signature. */}
      <div className='mb-5'>
        <SubsectionHeader icon={<PenLine className='h-3 w-3' />}>Signatures</SubsectionHeader>
        <div className='flex items-center justify-between py-2 border-b border-gray-100'>
          <span className='text-sm text-gray-600'>Technician</span>
          <SignaturePill
            url={event.technician_signature_url}
            onView={() => openLightbox(signatureImages, 0)}
          />
        </div>
        <div className='flex items-center justify-between py-2'>
          <span className='text-sm text-gray-600'>Dealer</span>
          <SignaturePill
            url={event.dealer_signature_url}
            onView={() => openLightbox(signatureImages, event.technician_signature_url ? 1 : 0)}
          />
        </div>
      </div>

      {hasPhotos && (
        <div className='mb-5'>
          <div className='mb-2 flex items-center justify-between'>
            <SubsectionHeader icon={<ImageIcon className='h-3 w-3' />}>
              Work Photos
            </SubsectionHeader>
            <span
              className='text-xs font-medium text-gray-600'
              style={{ fontVariantNumeric: 'tabular-nums' }}
            >
              {photos.length}
            </span>
          </div>
          <div className='flex flex-wrap gap-2'>
            {photos.map((url, i) => (
              <button
                key={url}
                type='button'
                onClick={() => openLightbox(photos, i)}
                className={[
                  'h-[72px] w-[72px] overflow-hidden rounded-lg ring-1 ring-gray-200',
                  'transition hover:ring-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500'
                ].join(' ')}
                aria-label={`Open photo ${i + 1}`}
              >
                <img
                  src={url}
                  alt={`Captured at completion (${i + 1})`}
                  className='h-full w-full object-cover'
                  loading='lazy'
                />
              </button>
            ))}
          </div>
        </div>
      )}

      {hasNotes && (
        <div>
          <SubsectionHeader icon={<FileText className='h-3 w-3' />}>Notes</SubsectionHeader>
          <p className='whitespace-pre-wrap text-sm leading-relaxed text-gray-800'>{notes}</p>
        </div>
      )}

      <PhotoLightbox
        photos={lightboxImages}
        startIndex={lightboxStart}
        open={lightboxOpen}
        onOpenChange={setLightboxOpen}
      />
    </section>
  );
}
